import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "../TabletButton";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions";
import Box from "@material-ui/core/Box";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";
import ButtonWithSpinner from "../ButtonWithSpinner";
import UserAvatar from "../UserAvatar";
import AvatarUploadModal from "../AvatarUploadModal";
import TextFieldWithErrorTooltip from "../TextFieldWithErrorTooltip";
import { isBillingRestricted } from "../../utils/billing";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	topSection: {
		display: "flex",
		gap: theme.spacing(2),
		marginBottom: theme.spacing(1),
	},
	fieldsColumn: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
	},
	avatarColumn: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "center",
		paddingTop: theme.spacing(1),
	},
	avatarWrapper: {
		position: "relative",
		cursor: "pointer",
		"&:hover $avatarOverlay": {
			opacity: 1,
		},
	},
	avatarOverlay: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "rgba(0, 0, 0, 0.5)",
		borderRadius: "50%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		opacity: 0,
		transition: "opacity 0.2s ease",
	},
	cameraIcon: {
		color: "#fff",
		fontSize: "2.5rem",
	},
	avatar: {
		width: 144,
		height: 144,
		backgroundColor: theme.palette.primary.main,
		fontSize: "3.5rem",
		fontWeight: 600,
	},
	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	permissionsGrid: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(1),
		width: "100%",
	},
	permissionItem: {
		flex: "1 1 calc(50% - 8px)",
		minWidth: 0,
		margin: 0,
		minHeight: 44,
	},
	dialogActions: {
		padding: theme.spacing(2),
		justifyContent: "space-between",
	},
	rightButtons: {
		display: "flex",
		gap: theme.spacing(1),
	},
	saveButton: {
		...theme.neoUI?.buttons?.action?.root,
		height: "32.5px",
	},
	cancelButton: {
		...theme.neoUI.buttons.action.cancel.root,
		height: "32.5px",
	},
	paper: {
		height: "auto",
		maxHeight: "90vh",
		display: "flex",
		flexDirection: "column",
	},
	dialogContent: {
		padding: "8px 24px",
		overflow: "visible",
		flex: "unset",
	},
}));
const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, i18n.t("userModal.formErrors.name.short"))
		.max(50, i18n.t("userModal.formErrors.name.long"))
		.required(i18n.t("userModal.formErrors.name.required")),
	password: Yup.string().min(5, i18n.t("userModal.formErrors.password.short")).max(50, i18n.t("userModal.formErrors.password.long")),
	email: Yup.string().email(i18n.t("userModal.formErrors.email.invalid")).required(i18n.t("userModal.formErrors.email.required")),
});

const UserModal = ({ open, onClose, userId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		email: "",
		password: "",
		profile: "user",
		allTicket: "desabled",
		viewPendingTickets: "disabled",
		companyQuickMessages: "disabled",
		allowTagEdit: "disabled"
	};

	const { user: loggedInUser, billing } = useContext(AuthContext);
	const restrictedSelfProfile = String(userId) === String(loggedInUser.id) && isBillingRestricted(loggedInUser, billing);

	const [user, setUser] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [whatsappId, setWhatsappId] = useState(false);
	const [avatarModalOpen, setAvatarModalOpen] = useState(false);
	const [nonAdminPermissions, setNonAdminPermissions] = useState({
		allTicket: "desabled",
		viewPendingTickets: "disabled",
		companyQuickMessages: "disabled",
		allowTagEdit: "disabled"
	});
	const { loading, whatsApps } = useWhatsApps();

	useEffect(() => {
		const fetchUser = async () => {
			if (!userId) return;
			try {
				const { data } = await api.get(`/users/${userId}`);
				setUser(prevState => {
					const normalizedProfile = (data.profile || "").toLowerCase();
					const isAdminProfile = normalizedProfile === "admin";
					const resolvedAllTicket = isAdminProfile ? "enabled" : (data.allTicket ?? "desabled");
					const resolvedViewPendingTickets = isAdminProfile ? "enabled" : (data.viewPendingTickets ?? "disabled");
					const resolvedCompanyQuickMessages = isAdminProfile ? "enabled" : (data.companyQuickMessages ?? "disabled");
					const resolvedAllowTagEdit = isAdminProfile ? "enabled" : (data.allowTagEdit ?? "disabled");
					if (!isAdminProfile) {
						setNonAdminPermissions({
							allTicket: resolvedAllTicket,
							viewPendingTickets: resolvedViewPendingTickets,
							companyQuickMessages: resolvedCompanyQuickMessages,
							allowTagEdit: resolvedAllowTagEdit
						});
					} else {
						setNonAdminPermissions({ allTicket: "desabled", viewPendingTickets: "disabled", companyQuickMessages: "disabled", allowTagEdit: "disabled" });
					}
					return {
						...prevState,
						...data,
						allTicket: resolvedAllTicket,
						viewPendingTickets: resolvedViewPendingTickets,
						companyQuickMessages: resolvedCompanyQuickMessages,
						allowTagEdit: resolvedAllowTagEdit
					};
				});
				const userQueueIds = data.queues?.map(queue => queue.id) || [];
				// Add -1 (Sem Fila) if user has allowNoQueue permission
				const finalQueueIds = data.allowNoQueue
					? [...userQueueIds, -1]
					: userQueueIds;
				setSelectedQueueIds(finalQueueIds);
				setWhatsappId(data.whatsappId ? data.whatsappId : '');
			} catch (err) {
				toastError(err);
			}
		};

		fetchUser();
	}, [userId, open]);

	const handleClose = () => {
		onClose();
		setUser(initialState);
		setNonAdminPermissions({ allTicket: "desabled", viewPendingTickets: "disabled", companyQuickMessages: "disabled", allowTagEdit: "disabled" });
	};

	const handleSaveUser = async values => {
		const isAdminProfile = (values.profile || "").toLowerCase() === "admin";
		const userData = restrictedSelfProfile
			? Object.fromEntries(["name", "email", "password", "language"].filter(key => values[key] !== undefined).map(key => [key, values[key]]))
			: {
			...values,
			whatsappId,
			queueIds: selectedQueueIds,
			allTicket: isAdminProfile ? "enabled" : values.allTicket,
			viewPendingTickets: isAdminProfile ? "enabled" : values.viewPendingTickets,
			companyQuickMessages: isAdminProfile ? "enabled" : values.companyQuickMessages,
			allowTagEdit: isAdminProfile ? "enabled" : values.allowTagEdit
		};
		try {
			if (userId) {
				await api.put(`/users/${userId}`, userData);
			} else {
				await api.post("/users", userData);
			}
			toast.success(i18n.t("userModal.success"));
		} catch (err) {
			toastError(err);
		}
		handleClose();
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth={false}
				aria-labelledby="form-dialog-title"
				classes={{
					paper: classes.paper
				}}
				PaperProps={{
					style: { maxWidth: '500px', width: '100%' }
				}}
				scroll="paper"
			>
				<DialogTitle id="form-dialog-title">
					{userId
						? `${i18n.t("userModal.title.edit")}`
						: `${i18n.t("userModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveUser(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form>
							<DialogContent className={classes.dialogContent}>
								{/* Seção superior: campos à esquerda, avatar à direita */}
								<div className={classes.topSection}>
									<div className={classes.fieldsColumn}>
										<Field
											as={TextFieldWithErrorTooltip}
											label={i18n.t("userModal.form.name")}
											autoFocus
											name="name"
											error={touched.name && Boolean(errors.name)}
											helperText={touched.name && errors.name}
											variant="outlined"
											margin="dense"
											fullWidth
										/>
										<Field
											as={TextFieldWithErrorTooltip}
											label={i18n.t("userModal.form.email")}
											name="email"
											error={touched.email && Boolean(errors.email)}
											helperText={touched.email && errors.email}
											variant="outlined"
											margin="dense"
											fullWidth
										/>
										<Field
											as={TextFieldWithErrorTooltip}
											label={i18n.t("userModal.form.password")}
											type="password"
											name="password"
											error={touched.password && Boolean(errors.password)}
											helperText={touched.password && errors.password}
											variant="outlined"
											margin="dense"
											fullWidth
										/>
									</div>
									<div className={classes.avatarColumn}>
										<Tooltip title={userId ? i18n.t("userModal.changeAvatar") : ""}>
											<div
												className={classes.avatarWrapper}
												onClick={() => userId && setAvatarModalOpen(true)}
												style={{ cursor: userId ? "pointer" : "default" }}
											>
												<UserAvatar
													user={{ name: values.name, profileImage: user.profileImage, isGhostUser: user.isGhostUser }}
													className={classes.avatar}
													style={{ color: "#fff" }}
												/>
												{userId && (
													<div className={classes.avatarOverlay}>
														<PhotoCameraIcon className={classes.cameraIcon} />
													</div>
												)}
											</div>
										</Tooltip>
									</div>
								</div>

								{!restrictedSelfProfile && <>
								{/* Linha: Perfil + Conexão Padrão */}
								<div className={classes.multFieldLine}>
									<FormControl
										variant="outlined"
										margin="dense"
										fullWidth
									>
										<Can
											role={loggedInUser.profile}
											perform="user-modal:editProfile"
											yes={() => (
												<>
													<InputLabel id="profile-selection-input-label">
														{i18n.t("userModal.form.profile")}
													</InputLabel>

													<Field
														as={Select}
														label={i18n.t("userModal.form.profile")}
														name="profile"
														labelId="profile-selection-label"
														id="profile-selection"
														required
														onChange={(event) => {
															const value = event.target.value;
															setFieldValue("profile", value);
															if (value === "admin") {
																setNonAdminPermissions({
																	allTicket: values.allTicket || "desabled",
																	viewPendingTickets: values.viewPendingTickets || "disabled",
																	companyQuickMessages: values.companyQuickMessages || "disabled",
																	allowTagEdit: values.allowTagEdit || "disabled"
																});
																setFieldValue("allTicket", "enabled");
																setFieldValue("viewPendingTickets", "enabled");
																setFieldValue("companyQuickMessages", "enabled");
																setFieldValue("allowTagEdit", "enabled");
															} else {
																setFieldValue("allTicket", nonAdminPermissions.allTicket || "desabled");
																setFieldValue("viewPendingTickets", nonAdminPermissions.viewPendingTickets || "disabled");
																setFieldValue("companyQuickMessages", nonAdminPermissions.companyQuickMessages || "disabled");
																setFieldValue("allowTagEdit", nonAdminPermissions.allowTagEdit || "disabled");
															}
														}}
													>
														<MenuItem value="admin">Admin</MenuItem>
														<MenuItem value="user">User</MenuItem>
													</Field>
												</>
											)}
										/>
									</FormControl>
									<Can
										role={loggedInUser.profile}
										perform="user-modal:editProfile"
										yes={() => (
											<FormControl variant="outlined" margin="dense" fullWidth>
												<InputLabel>
													{i18n.t("userModal.form.whatsapp")}
												</InputLabel>
												<Field
													as={Select}
													value={whatsappId}
													onChange={(e) => setWhatsappId(e.target.value)}
													label={i18n.t("userModal.form.whatsapp")}
												>
													<MenuItem value={''}>&nbsp;</MenuItem>
													{whatsApps.map((whatsapp) => (
														<MenuItem key={whatsapp.id} value={whatsapp.id}>{whatsapp.name}</MenuItem>
													))}
												</Field>
											</FormControl>
										)}
									/>
								</div>
								<Can
									role={loggedInUser.profile}
									perform="user-modal:editQueues"
									yes={() => (
										<QueueSelect
											selectedQueueIds={selectedQueueIds}
											onChange={values => setSelectedQueueIds(values)}
											showNoQueue={true}
										/>
									)}
								/>



								<div className={classes.divider}>
									<span className={classes.dividerText}>
										{i18n.t("userModal.labels.liberations")}
									</span>
								</div>

								<Can
									role={loggedInUser.profile}
									perform="user-modal:editProfile"
									yes={() => (!loading &&
										<div className={classes.permissionsGrid}>
											{[
												{ name: "allTicket", disabledValue: "desabled" },
												{ name: "viewPendingTickets", disabledValue: "disabled" },
												{ name: "allowTagEdit", disabledValue: "disabled" },
												{ name: "companyQuickMessages", disabledValue: "disabled" }
											].map(({ name, disabledValue }) => (
												<FormControlLabel
													key={name}
													className={classes.permissionItem}
													label={i18n.t(`userModal.form.${name}`)}
													control={<Switch
														color="primary"
														checked={values[name] === "enabled"}
														disabled={(values.profile || "").toLowerCase() === "admin"}
														onChange={event => setFieldValue(name, event.target.checked ? "enabled" : disabledValue)}
													/>}
												/>
											))}
										</div>

									)}
								/>

								</>}
							</DialogContent>
							<DialogActions className={classes.dialogActions}>
								<Box />
								<Box className={classes.rightButtons}>
									<Button tabletSubject="user"
										onClick={handleClose}
										color="secondary"
										variant="outlined"
										className={classes.cancelButton}
									>
										{i18n.t("userModal.buttons.cancel")}
									</Button>
									<ButtonWithSpinner tabletSubject="user"
										loading={isSubmitting}
										color="primary"
										variant="contained"
										type="submit"
										disabled={isSubmitting}
										className={classes.saveButton}
									>
										{userId
											? i18n.t("userModal.buttons.okEdit")
											: i18n.t("userModal.buttons.okAdd")}
									</ButtonWithSpinner>
								</Box>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
			{userId && (
				<AvatarUploadModal
					open={avatarModalOpen}
					onClose={() => setAvatarModalOpen(false)}
					userId={userId}
					currentImage={user.profileImage}
					onSuccess={(newProfileImage) => {
						setUser(prev => ({ ...prev, profileImage: newProfileImage }));
					}}
					onDelete={() => {
						setUser(prev => ({ ...prev, profileImage: null }));
					}}
				/>
			)}
		</>
	);
};

export default UserModal;
